import React, { useEffect } from 'react';
import { FaUser, FaUserTie, FaUsers, FaChevronRight, FaArrowLeft, FaCog } from 'react-icons/fa';
import '../scss/ProfileSelection.scss';
import logo from '../images/logo-long.svg';

function ProfileSelection({
  profiles,
  onSelectProfile,
  onClose,
  title = 'Select Profile',
  subtitle = 'We found multiple accounts. Which profile would you like to use?',
  currentProfile = null,
  disabled = false,
  extraActions = [],
}) {
  // Check if running on Android/Capacitor
  const isCapacitor = window.Capacitor?.isNativePlatform() || false;
  const isAndroid = isCapacitor && window.Capacitor?.getPlatform() === 'android';

  useEffect(() => {
    // Add Android class to body for CSS targeting
    if (isAndroid) {
      document.body.classList.add('android-device');
    }
    
    return () => {
      // Don't remove the class on unmount as other components might need it
      // The Header component will manage it when it mounts
    };
  }, [isAndroid]);
  const getIcon = (userType) => {
    switch (userType) {
      case 'parent':
        return <FaUsers className="profile-icon" />;
      case 'coach':
      case 'manager':
        return <FaUserTie className="profile-icon" />;
      default:
        return <FaUser className="profile-icon" />;
    }
  };

  const getRoleColor = (userType) => {
    switch (userType) {
      case 'parent':
        return 'parent';
      case 'coach':
      case 'manager':
        return 'coach';
      default:
        return 'player';
    }
  };

  const normaliseUserType = (type) => {
    if (type === 'manager') return 'coach';
    return type || 'player';
  };

  const formatName = (profile) => {
    if (profile.first_name && profile.last_name) {
      const firstName = profile.first_name.charAt(0).toUpperCase() + profile.first_name.slice(1).toLowerCase();
      const lastName = profile.last_name.charAt(0).toUpperCase() + profile.last_name.slice(1).toLowerCase();
      return `${firstName} ${lastName}`;
    }
    // Fallback to display_name if first_name/last_name not available
    return profile.display_name || '';
  };

  const formatRole = (profile) => {
    if (normaliseUserType(profile.user_type) === 'coach') {
      return 'Coach/Manager';
    }
    return profile.role || '';
  };

  const isCurrentProfile = (profile) => {
    if (!currentProfile) return false;
    return normaliseUserType(profile.user_type) === normaliseUserType(currentProfile.user_type)
      && String(profile.user_id) === String(currentProfile.user_id);
  };

  return (
    <div className="ProfileSelection container">
      <header className="header">
        <div className="logo-container">
          <img 
            src={logo} 
            alt="Logo" 
            className="logo centered-logo" 
          />
        </div>
      </header>
      
      <div className="profile-selection-content">
        {onClose && (
          <button className="back-button" onClick={onClose}>
            <FaArrowLeft /> Back
          </button>
        )}
        <h1>{title}</h1>
        <p className="subtitle">{subtitle}</p>
        
        <div className="profiles-list">
          {profiles.map((profile) => {
            const isCurrent = isCurrentProfile(profile);
            return (
            <button
              type="button"
              key={`${profile.user_type}-${profile.user_id}`}
              className={`profile-card profile-${getRoleColor(profile.user_type)}${isCurrent ? ' is-current' : ''}`}
              onClick={() => onSelectProfile(profile)}
              disabled={disabled}
            >
              <div className="profile-icon-wrapper">
                {getIcon(profile.user_type)}
              </div>
              <div className="profile-info">
                <h3>{formatName(profile)}</h3>
                <p className="profile-role">{formatRole(profile)}</p>
                {isCurrent && (
                  <p className="profile-detail">Currently using</p>
                )}
                {profile.shirt_number && (
                  <p className="profile-detail">Shirt # {profile.shirt_number}</p>
                )}
              </div>
              <div className="profile-arrow">
                <FaChevronRight />
              </div>
            </button>
            );
          })}
        </div>
        {extraActions.length > 0 && (
          <div className="academy-settings-list">
            {extraActions.map((action) => (
              <button
                type="button"
                key={action.id}
                className="profile-card profile-academy"
                onClick={action.onClick}
                disabled={disabled}
              >
                <div className="profile-icon-wrapper">
                  <FaCog className="profile-icon" />
                </div>
                <div className="profile-info">
                  <h3>{action.title}</h3>
                  <p className="profile-role">{action.subtitle}</p>
                </div>
                <div className="profile-arrow">
                  <FaChevronRight />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileSelection;

